import {
  ICreateSessionRequest,
  IUpdateSessionRequest,
  MatchingSession,
} from '@data/session/session.model';
import { SessionActions } from '@data/session/session.actions';
import { ContextMenuOption } from '@shared/menus/context-menu/context-menu-option/context-menu-option';
import { SessionStatus } from '@core/enums/session-status';
import { NotificationService } from '@core/services/notification/notification.service';
import { DialogService } from '@shared/components/dialog/dialog.service';
import { IonosFacadeService } from '@core/services/upload/ionos-facade.service';
import {
  SessionDialogComponent,
  SessionDialogResponse,
} from '../../../../modules/sessions/component/session-dialog/session-dialog.component';
import { SyncDialogComponent } from '../../../../modules/sessions/component/sync-dialog/sync-dialog.component';
import { SessionJobDatasetService } from '@data/session-job-dataset/session-job-dataset.service';
import { SessionJobDatasetActions } from '@data/session-job-dataset/session-job-dataset.actions';

export namespace MatchingSessionOptions {
  export class EditSessionOption extends ContextMenuOption<MatchingSession> {
    constructor(
      target: MatchingSession,
      private dialog: DialogService<SessionDialogComponent, SessionDialogResponse>,
      private upload: IonosFacadeService,
      private sessionJobDataService: SessionJobDatasetService,
    ) {
      super('edit', 'SESSIONS.TABLE.ACTIONS.edit', target);
    }

    onClick(): void {
      if (this.target) {
        this.editSession(this.target);
      }
    }

    editSession(session: MatchingSession) {
      this.dialog.openWithCallback({
        type: SessionDialogComponent,
        dialogData: session,
        confirmCallback: (result) => {
          if (!result) return;
          const logo = result.value.image;
          if (!logo) {
            this.upload.removeDisplayImage('fairs', session.id, 'fair/logo');
          } else if (typeof logo !== 'string') {
            this.upload.uploadDisplayImage('fairs', 'fair/logo', session.id, logo).subscribe(() => {
              const imageUrl = `https://v2.api.anymatch.de/api/web/files/fairs/fair/logo/${session.id}/logo`;
              SessionActions.updateSession(session.id, { image: imageUrl });
              setTimeout(() => location.reload(), 2000);
            });
          }

          const update = { ...result.value } as IUpdateSessionRequest & ICreateSessionRequest;
          delete update.image;
          delete update.pin;
          delete update.mode;

          const datasetValue = result.datasetValue;
          if (session.jobDataset) {
            SessionJobDatasetActions.updateDataset(session.jobDataset, datasetValue);
            SessionActions.updateSession(session.id, update);
          } else {
            this.sessionJobDataService
              .create({
                ...datasetValue,
                name: session.name,
                team: session.team,
                coursesGeneral: [],
              })
              .subscribe((it) =>
                SessionActions.updateSession(session.id, { ...update, jobDataset: it?._id }),
              );
          }
          setTimeout(() => location.reload(), 2000);
        },
      });
    }
  }
  export class CloseMatchSessionOption extends ContextMenuOption<MatchingSession> {
    constructor(target: MatchingSession) {
      super('close', 'SESSIONS.TABLE.ACTIONS.close', target);
    }

    onClick(): void {
      if (this.target) {
        this.closeSession(this.target);
      }
    }

    closeSession(session: MatchingSession) {
      SessionActions.closeSession(session.id);
    }
  }
  export class StartMatchSessionOption extends ContextMenuOption<MatchingSession> {
    constructor(target: MatchingSession) {
      super('play_arrow', 'SESSIONS.TABLE.ACTIONS.start', target);
    }

    onClick(): void {
      if (this.target) {
        this.startSession(this.target);
      }
    }

    startSession(session: MatchingSession) {
      SessionActions.startSession(session.id);
    }
  }

  export class ShareSessionOption extends ContextMenuOption<MatchingSession> {
    constructor(
      target: MatchingSession,
      private notifier: NotificationService,
    ) {
      super('share', 'Beitrittslink kopieren', target);
    }

    onClick(): void {
      if (this.target) {
        const url =
          'https://' +
          window.location.host +
          '/public/join?s=' +
          this.target.id +
          '&pin=' +
          this.target.pin;
        navigator.clipboard
          .writeText(url)
          .then(() => this.notifier.success('Beitrittslink kopiert'));
      }
    }
  }

  /**
   * Opens the one-way "Sync to RECREWT" dialog for the session. The dialog is fully
   * self-contained (injects its own {@link SyncService}), so this option only needs the
   * shared {@link DialogService} to launch it with the session as dialog data.
   */
  export class SyncToRecrewtOption extends ContextMenuOption<MatchingSession> {
    constructor(
      target: MatchingSession,
      private dialog: DialogService<SyncDialogComponent, void>,
    ) {
      super('cloud_upload', 'SESSIONS.TABLE.ACTIONS.sync', target);
    }

    onClick(): void {
      if (this.target) {
        this.dialog.open(SyncDialogComponent, this.target);
      }
    }
  }

  export function getSessionMenuOptions(
    session: MatchingSession,
    dialog: DialogService<any, any>,
    notifier: NotificationService,
    upload: IonosFacadeService,
    sessionJobDataService: SessionJobDatasetService,
  ): ContextMenuOption<MatchingSession>[] {
    const r: ContextMenuOption<MatchingSession>[] = [];
    r.push(new EditSessionOption(session, dialog, upload, sessionJobDataService));
    if (session.status === SessionStatus.NEW || session.status === SessionStatus.ACTIVE) {
      r.push(new CloseMatchSessionOption(session));
    }
    if (session.status === SessionStatus.NEW) {
      r.push(new StartMatchSessionOption(session));
    }
    r.push(new ShareSessionOption(session, notifier));
    r.push(new SyncToRecrewtOption(session, dialog));
    return r;
  }
}
